import cx from 'classnames'
import { useTranslation } from 'next-i18next'
import { numberToCurrency } from 'utils'

import { Product } from '../../hooks/useAcreCalculator'

/** The pass-through monthly costs added on top of a financing base. */
export type PaymentExtras = {
  taxMonthly: number
  insuranceMonthly: number
  /** Null when the listing has no HOA; counted as $0. */
  hoaMonthly: number | null
  /** v3HoaDisplay: when false, HOA is left out of the totals and its row hidden. */
  showHoa: boolean
}

export const roundHalfUp = (value: number) => Math.floor(value + 0.5)

export const roundedExtras = ({
  taxMonthly,
  insuranceMonthly,
  hoaMonthly,
  showHoa,
}: PaymentExtras) => ({
  tax: roundHalfUp(taxMonthly),
  insurance: roundHalfUp(insuranceMonthly),
  hoa: showHoa ? roundHalfUp(hoaMonthly ?? 0) : 0,
})

/**
 * The "Monthly payment breakdown" body: the selected Acre product versus a
 * mortgage, base and total per column, with taxes, insurance and HOA as shared
 * rows spanning both. Matches the calculator's pane (PDE-87). The caller
 * supplies the heading. By default the body carries the calculator pane's own
 * insets (px-16 on mobile; from lg a full-width table with 24px text insets);
 * with `flush` it adds none, and sits inside its container's padding.
 */
const MonthlyPaymentBreakdown = ({
  product,
  productBase,
  mortgageBase,
  extras,
  flush = false,
}: {
  product: Product
  productBase: number
  mortgageBase: number
  extras: PaymentExtras
  /**
   * The container supplies the padding: no inset of the body's own at any
   * width, so the intro, table, rules and footnote share its edges.
   */
  flush?: boolean
}) => {
  const { t } = useTranslation('homes')
  const { tax, insurance, hoa } = roundedExtras(extras)
  const base = roundHalfUp(productBase)
  const mortgage = roundHalfUp(mortgageBase)
  const total = base + tax + insurance + hoa
  const mortgageTotal = mortgage + tax + insurance + hoa
  const savings = mortgageTotal - total

  const ruleRow = cx(
    'border-b',
    product === 'saver' ? 'border-teal-3' : 'border-tan-3'
  )
  // Row labels, cells and column headers use the font's normal line height
  // (the page's body line height is 1.5).
  const rowLabel = (weight: 'font-semibold' | 'font-normal', top = false) =>
    cx(
      'whitespace-nowrap py-12 pl-0 pr-8 text-left text-14 leading-[normal] lg:py-16 lg:text-16',
      weight,
      { 'align-top': top, 'lg:px-24': !flush }
    )
  const cell =
    'px-6 py-12 text-center text-16 font-semibold leading-[normal] lg:py-16'
  const productCell = cx(
    cell,
    product === 'saver' ? 'bg-[#E8F2EE]' : 'bg-[#FFE8D6]'
  )
  const colHead =
    'whitespace-nowrap px-6 pb-10 pt-[11px] text-center align-top text-12 font-semibold leading-[normal] lg:pb-12 lg:text-16'

  return (
    // Unless flush: px-16 on mobile; from lg the table spans the full width
    // and only the text lines keep the 24px inset. 3px of top padding
    // (padding, so it cannot collapse with the heading's mb-8) makes 11px
    // above the intro.
    <div className={cx({ 'px-16 pb-24 pt-[3px] lg:p-0': !flush })}>
      <p
        className={cx(
          'text-12 leading-[17px] text-grey-8 lg:text-14 lg:leading-5',
          { 'lg:mt-[11px] lg:px-24': !flush }
        )}
      >
        {t('paymentBreakdown.intro')}
      </p>
      <table className="w-full border-collapse lg:table-fixed">
        <colgroup>
          <col className="w-[30%] lg:w-1/2" />
          <col className="w-[35%] lg:w-1/4" />
          <col className="w-[35%] lg:w-1/4" />
        </colgroup>
        <thead>
          <tr>
            <td></td>
            <th scope="col" className={colHead}>
              <p>{product === 'saver' ? t('acreSaver') : t('acreBoost')}</p>
            </th>
            <th scope="col" className={colHead}>
              <p>{t('mortgage')}</p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr className={ruleRow}>
            <th scope="row" className={rowLabel('font-semibold')}>
              {t('paymentBreakdown.basePayment')}
            </th>
            <td className={productCell}>{numberToCurrency(base)}</td>
            <td className={cell}>{numberToCurrency(mortgage)}</td>
          </tr>
          <tr>
            <th scope="row" className={rowLabel('font-normal')}>
              {t('paymentBreakdown.propertyTaxes')}
            </th>
            <td colSpan={2} className={cell}>
              {numberToCurrency(tax)}
            </td>
          </tr>
          <tr className={cx({ [ruleRow]: !extras.showHoa })}>
            <th scope="row" className={rowLabel('font-normal')}>
              {t('paymentBreakdown.insurance')}
            </th>
            <td colSpan={2} className={cell}>
              {numberToCurrency(insurance)}
            </td>
          </tr>
          {extras.showHoa && (
            <tr className={ruleRow}>
              <th scope="row" className={rowLabel('font-normal')}>
                {t('paymentBreakdown.hoaFees')}
              </th>
              <td colSpan={2} className={cell}>
                {numberToCurrency(hoa)}
              </td>
            </tr>
          )}
          <tr>
            <th scope="row" className={rowLabel('font-semibold', true)}>
              {t('paymentBreakdown.totalPayment')}
            </th>
            <td className={cx(cell, 'align-top')}>
              {numberToCurrency(total)}
              {savings > 0 && (
                <span
                  className={cx(
                    'mt-[3px] block whitespace-nowrap text-14 font-normal italic',
                    product === 'saver' ? 'text-[#0B8381]' : 'text-[#A46442]'
                  )}
                >
                  {t('paymentBreakdown.save', {
                    amount: numberToCurrency(savings),
                  })}
                </span>
              )}
            </td>
            <td className={cx(cell, 'align-top')}>
              {numberToCurrency(mortgageTotal)}
            </td>
          </tr>
        </tbody>
      </table>
      <p className={cx('mt-24 text-14 leading-4', { 'lg:px-24': !flush })}>
        {t('paymentBreakdown.footnote')}
      </p>
    </div>
  )
}

export default MonthlyPaymentBreakdown
